import React, { useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    Tooltip
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import BookmarkIcon from '@material-ui/icons/Bookmark'

const readIdentity = (entity) => {
    const ident = entity?.exclusionIdentification || {}
    const details = entity?.exclusionDetails || {}
    const addr = entity?.exclusionPrimaryAddress || {}
    const displayName = ident.entityName
        || [ident.firstName, ident.middleName, ident.lastName, ident.suffix].filter(Boolean).join(' ').trim()
        || '(unnamed)'
    // Build a stable-ish key that identifies THIS exclusion record
    const matchedKey = ident.ueiSAM
        || ident.cageCode
        || ident.npi
        || [details.classificationType, ident.entityName, addr.stateOrProvinceCode, addr.zipCode].filter(Boolean).join('|')
        || displayName
    return {
        displayName,
        matchedKey,
        classification: details.classificationType || null,
        uei: ident.ueiSAM || null,
        cage: ident.cageCode || null,
        npi: ident.npi || null,
        city: addr.city || null,
        state: addr.stateOrProvinceCode || null,
        country: addr.countryCode || null,
    }
}

const readExclusion = (entity) => {
    const details = entity?.exclusionDetails || {}
    const other = entity?.exclusionOtherInformation || {}
    const action = entity?.exclusionActions?.listOfActions?.[0] || {}
    return {
        exclusionType: details.exclusionType || null,
        exclusionProgram: details.exclusionProgram || null,
        excludingAgencyName: details.excludingAgencyName || null,
        excludingAgencyCode: details.excludingAgencyCode || null,
        createDate: action.createDate || null,
        updateDate: action.updateDate || null,
        activateDate: action.activateDate || null,
        terminationDate: action.terminationDate || null,
        terminationType: action.terminationType || null,
        recordStatus: action.recordStatus || null,
        additionalComments: other.additionalComments || null,
        isFascsaOrder: (other.isFASCSAOrder || '').toString().toLowerCase() === 'yes',
    }
}

const ExpandableRow = ({ entity, index, onSave, isSaved, saveEnabled }) => {
    const [open, setOpen] = useState(false)
    const meta = readIdentity(entity)
    const exc = readExclusion(entity)

    const savedTooltip = isSaved ? 'Already saved to this application' : 'Save this match to the application'
    const disabledTooltip = 'Only available inside an application'

    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>
                    {meta.displayName}
                    {exc.isFascsaOrder && (
                        <span
                            title='Federal Acquisition Supply Chain Security order'
                            style={{
                                marginLeft: 6, padding: '1px 6px', fontSize: 10,
                                background: '#fce4ec', color: '#ad1457',
                                borderRadius: 3, fontWeight: 600, verticalAlign: 'middle'
                            }}
                        >
                            FASCSA
                        </span>
                    )}
                </TableCell>
                <TableCell>{meta.classification || '—'}</TableCell>
                <TableCell>{exc.exclusionType || '—'}</TableCell>
                <TableCell>{exc.activateDate || '—'}</TableCell>
                <TableCell>{exc.terminationDate || '—'}</TableCell>
                <TableCell>{meta.state || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    {onSave && (
                        <Tooltip title={saveEnabled ? savedTooltip : disabledTooltip} arrow>
                            <span>
                                <IconButton
                                    size='small'
                                    disabled={!saveEnabled || isSaved}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (!isSaved) onSave({ meta, exc, entity })
                                    }}
                                    aria-label={isSaved ? 'saved' : 'save'}
                                    id={`samgov-save-${index}`}
                                >
                                    {isSaved ? <BookmarkIcon fontSize='small' color='primary'/> : <BookmarkBorderIcon fontSize='small'/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        id={`samgov-toggle-${index}`}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div><strong>Excluding Agency:</strong> {exc.excludingAgencyName || '—'} {exc.excludingAgencyCode ? `(${exc.excludingAgencyCode})` : ''}</div>
                            <div><strong>Program:</strong> {exc.exclusionProgram || '—'}</div>
                            <div><strong>Record Status:</strong> {exc.recordStatus || '—'} &nbsp;&nbsp; <strong>Termination Type:</strong> {exc.terminationType || '—'}</div>
                            <div><strong>First Published:</strong> {exc.createDate || '—'} &nbsp;&nbsp; <strong>Last Updated:</strong> {exc.updateDate || '—'}</div>
                            <div><strong>Location:</strong> {[meta.city, meta.state, meta.country].filter(Boolean).join(', ') || '—'}</div>
                            <div><strong>UEI:</strong> {meta.uei || '—'} &nbsp;&nbsp; <strong>CAGE:</strong> {meta.cage || '—'} &nbsp;&nbsp; <strong>NPI:</strong> {meta.npi || '—'}</div>
                            {exc.isFascsaOrder && (
                                <div style={{ marginTop: 6 }}><strong>FASCSA Order:</strong> Yes — Federal Acquisition Supply Chain Security</div>
                            )}
                            {exc.additionalComments && (
                                <div style={{ marginTop: 6 }}><strong>Comments:</strong> {exc.additionalComments}</div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const SamgovResultsTable = ({ entities, onSave, savedKeys = new Set(), saveEnabled = false }) => {
    return (
        <TableContainer component={Paper} className='mt-3'>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Activated</TableCell>
                        <TableCell>Terminates</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entities.map((entity, i) => {
                        const meta = readIdentity(entity)
                        return (
                            <ExpandableRow
                                key={meta.matchedKey || i}
                                entity={entity}
                                index={i}
                                onSave={onSave}
                                isSaved={savedKeys.has(meta.matchedKey)}
                                saveEnabled={saveEnabled}
                            />
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default SamgovResultsTable
